import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import ReportRoundedIcon from '@mui/icons-material/ReportRounded'
import SyncProblemRoundedIcon from '@mui/icons-material/SyncProblemRounded'
import Box from '@mui/material/Box'
import Card, { CardProps } from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { SiteDetailsApi } from '../../apis/siteDetails/siteDetailsApi'
import { MoovyButton, MoovyDialog } from '../../components'
import useAuth from '../../hooks/useAuth'
import { useSiteDetails } from '../../hooks/useSiteDetails'
import { MoovyToast } from '../../utils/moovyToast'
import { hasPermission } from '../permissions'
import FormChangeSystemStatus, { FormValues } from './formChangeSystemStatus'

export const FORM_REFERENCE_CHANGE_SYSTEM_STATUS = 'changeSystemStatusForm'

const SystemStatus = (props: CardProps) => {
  const intl = useIntl()
  const { isAdmin } = useAuth()
  const siteDetailsQuery = useSiteDetails()
  const siteDetails = siteDetailsQuery?.isSuccess
    ? siteDetailsQuery?.data?.data.data
    : undefined
  const isEnabled = !!siteDetails?.systemStatus?.isEnabled
  const [isModalOpen, setIsModalOpen] = useState(false)
  const openModal = () => {
    siteDetailsQuery?.setIsEnabled(false)
    setIsModalOpen(true)
  }
  const closeModal = () => {
    setIsModalOpen(false)
    siteDetailsQuery?.setIsEnabled(true)
    siteDetailsQuery?.refetch()
  }

  const changeStatusMutation = useMutation({
    mutationFn: SiteDetailsApi.changeSystemStatus,
    onSuccess() {
      closeModal()
      MoovyToast.showToast(
        intl.formatMessage({
          id: `common.system.changeStatusForm.success.${action.toLowerCase()}`,
        }),
        'success',
      )
    },
  })

  const onSubmit = (values: FormValues) => {
    changeStatusMutation.mutate(values)
  }
  const action = isEnabled ? 'DISABLE' : 'ENABLE'
  const statusIcon = {
    true: <CheckCircleRoundedIcon color="success" fontSize="large" />,
    false: <ReportRoundedIcon color="error" fontSize="large" />,
  }[JSON.stringify(siteDetails?.systemStatus?.isEnabled)] ?? (
    <SyncProblemRoundedIcon color="warning" fontSize="large" />
  )
  const statusTitle = {
    true: (
      <Typography variant="h6" color="success">
        <FormattedMessage id="common.system.enabledTitle" />
      </Typography>
    ),
    false: (
      <Typography variant="h6" color="error">
        <FormattedMessage id="common.system.disabledTitle" />
      </Typography>
    ),
  }[JSON.stringify(siteDetails?.systemStatus?.isEnabled)] ?? (
    <Typography variant="h6" color="warning">
      <FormattedMessage id="common.system.unknownTitle" />
    </Typography>
  )

  const statusDescription = {
    true: (
      <Typography>
        <FormattedMessage id="common.system.enabledDescription" />
      </Typography>
    ),
    false: (
      <Typography>
        <FormattedMessage id="common.system.disabledDescription" />
        {siteDetails?.systemStatus?.reason && (
          <>
            <br />
            <FormattedMessage
              id="common.system.disabledReason"
              values={{
                reason: (
                  <Box
                    component="span"
                    sx={{ fontStyle: 'italic', display: 'inline' }}
                  >
                    {siteDetails.systemStatus.reason}
                  </Box>
                ),
              }}
            />
          </>
        )}
      </Typography>
    ),
  }[JSON.stringify(siteDetails?.systemStatus?.isEnabled)] ?? (
    <Typography>
      <FormattedMessage id="common.system.unknownDescription" />
    </Typography>
  )

  return (
    <>
      <Card {...props}>
        <CardContent>
          <Stack
            direction={{ sm: 'column', md: 'row' }}
            spacing={2}
            height="100%"
            justifyContent="space-between"
            width="100%"
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="center"
            >
              <Stack
                direction="column"
                padding={1}
                alignItems="center"
                justifyContent="center"
              >
                {statusIcon}
              </Stack>
              <Stack direction="column" spacing={1} width="100%">
                {statusTitle}
                {statusDescription}
              </Stack>
            </Stack>
            {hasPermission(isAdmin, 'EDIT', 'SITE') &&
              typeof siteDetails?.systemStatus?.isEnabled === 'boolean' && (
                <Stack
                  direction="column"
                  alignItems="center"
                  justifyContent="center"
                >
                  <MoovyButton
                    onClick={() => {
                      openModal()
                    }}
                    color={isEnabled ? 'error' : 'success'}
                    style={{ color: 'white' }}
                    sx={{
                      width: { sm: '100%', xs: '100%' },
                      marginTop: { sm: 2, xs: 2 },
                    }}
                  >
                    <FormattedMessage
                      id={
                        isEnabled
                          ? 'common.system.disableButtonText'
                          : 'common.system.enableButtonText'
                      }
                    />
                  </MoovyButton>
                </Stack>
              )}
          </Stack>
        </CardContent>
      </Card>
      <MoovyDialog
        open={isModalOpen}
        onCancel={closeModal}
        formReference={FORM_REFERENCE_CHANGE_SYSTEM_STATUS}
        submitButtonContent={
          <FormattedMessage
            id={`common.system.changeStatusForm.submit.${action.toLowerCase()}`}
          />
        }
        modalTitle={
          <FormattedMessage
            id={`common.system.changeStatusModal.title.${action.toLowerCase()}`}
          />
        }
        mutation={changeStatusMutation}
      >
        <FormChangeSystemStatus
          action={action}
          onSubmit={onSubmit}
          formReference={FORM_REFERENCE_CHANGE_SYSTEM_STATUS}
          formTitleKey={`common.system.changeStatusForm.title.${action.toLowerCase()}`}
        />
      </MoovyDialog>
    </>
  )
}

export default SystemStatus
